import { createRouter } from "@tanstack/solid-router";
import { NotFoundRoute } from "@/components/not-found-route";
import { rootRoute } from "@/routes/__root";
import { authRoute } from "@/routes/_auth/route";
import { loginRoute } from "@/routes/_auth/login";
import { registerRoute } from "@/routes/_auth/register";
import { forgotPasswordRoute } from "@/routes/_auth/forgot-password";
import { resetPasswordRoute } from "@/routes/_auth/reset-password";
import { verifyEmailRoute } from "@/routes/_auth/verify-email";
import { socialCallbackRoute } from "@/routes/_auth/social-callback";
import { appRoute } from "@/routes/_app/route";
import { indexRoute } from "@/routes/_app/index";
import { mailRoute } from "@/routes/_app/mail";
import { mailComposeRoute } from "@/routes/_app/mail.compose";
import { mailSearchRoute } from "@/routes/_app/mail.search";
import { mailSettingsRoute } from "@/routes/_app/mail.settings";
import { profileRoute } from "@/routes/_app/profile";
import { debugRoute } from "@/routes/_app/debug";
import { organizationsRoute } from "@/routes/_app/organizations";
import { pluginRoutes } from "@/composition";
import { trashRoute } from "@/routes/_app/trash";
import { cc21ProbeRoute } from "@/routes/_app/cc21-probe";
import { organizationDetailRoute } from "@/routes/_app/organizations.$orgId";
import { invitationsRoute } from "@/routes/_app/invitations";
import { themeGalleryRoute } from "@/routes/_app/themes";
import { languageRoute } from "@/routes/_app/language";
import { profileFontsRoute } from "@/routes/_app/profile.fonts";
import { profileShortcutsRoute } from "@/routes/_app/profile.shortcuts";
import { profileDeleteAccountRoute } from "@/routes/_app/profile.delete-account";
import { profileTransferSendRoute } from "@/routes/_app/profile.transfer-send";
import { profileTransferReceiveRoute } from "@/routes/_app/profile.transfer-receive";

const routeTree = rootRoute.addChildren([
  socialCallbackRoute,
  authRoute.addChildren([
    loginRoute,
    registerRoute,
    forgotPasswordRoute,
    resetPasswordRoute,
    verifyEmailRoute,
  ]),
  appRoute.addChildren([
    // CC-21 regression fixture (Select/Popover reachable inside a modal
    // Dialog). DEV only: it serves the cc21-probe.spec e2e and is NOT registered in prod.
    ...(import.meta.env.DEV ? [cc21ProbeRoute] : []),
    indexRoute, // path: "/" — redirects to /mail
    mailRoute, // path: "/mail" — mailbox shell (folders + list + reader)
    mailComposeRoute, // path: "/mail/compose"
    mailSearchRoute, // path: "/mail/search"
    mailSettingsRoute, // path: "/mail/settings" — accounts + filter rules
    ...pluginRoutes,
    trashRoute,
    organizationsRoute,
    organizationDetailRoute,
    invitationsRoute,
    themeGalleryRoute,
    languageRoute,
    profileRoute,
    profileFontsRoute,
    profileShortcutsRoute,
    profileDeleteAccountRoute,
    profileTransferSendRoute,
    profileTransferReceiveRoute,
    debugRoute,
  ]),
]);

export const router = createRouter({
  routeTree,
  // "intent" preloads only when the user expresses intent (hover/focus on a
  // <Link>). The previous "viewport" setting eagerly fired `beforeLoad` and
  // PowerSync `resolveTables` EXPLAIN queries for ~20 sidebar routes the
  // moment the dashboard mounted. Combined with PowerSync's initial-sync
  // batches (each one re-invalidating subscribed queries), this saturated
  // the main thread for several seconds after login. Hover-only is the
  // standard tradeoff: a tiny first-hover latency in exchange for a calm
  // post-login render.
  defaultPreload: "intent",
  // oxlint-disable-next-line eslint(no-magic-numbers)
  defaultPreloadStaleTime: 30_000, // 30 s — don't re-preload the same route within 30 s
  // Unknown URL → "page not found" state with a CTA, instead of a blank screen.
  defaultNotFoundComponent: NotFoundRoute,
});

declare module "@tanstack/solid-router" {
  interface Register {
    router: typeof router;
  }
}
